import { useRef, type RefObject } from "react";

import { CloseIcon, SearchIcon } from "@/components/icons";

import styles from "./Field.module.css";

interface SearchFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** For a caller that puts the cursor here itself (the register's „/"). */
  inputRef?: RefObject<HTMLInputElement | null>;
}

/**
 * A search box in the field shape (UX ruling 2, F-9): a magnifier inside on
 * the left, and an own „Suche leeren" cross of 28 px instead of the native
 * one, which is 12 px and differs per browser. Escape empties it too.
 */
export function SearchField({ id, label, value, onChange, placeholder, inputRef }: SearchFieldProps) {
  const own = useRef<HTMLInputElement>(null);
  const ref = inputRef ?? own;
  return (
    <div className={styles.field}>
      <label className={`label ${styles.fieldLabel}`} htmlFor={id}>
        {label}
      </label>
      <div className={styles.searchWrap}>
        <SearchIcon className={styles.searchIcon} />
        <input
          ref={ref}
          id={id}
          type="search"
          className={`${styles.input} ${styles.searchInput}`}
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape" && value !== "") {
              event.preventDefault();
              onChange("");
            }
          }}
        />
        {value !== "" ? (
          <button
            type="button"
            className={styles.clear}
            aria-label="Suche leeren"
            onClick={() => {
              onChange("");
              ref.current?.focus();
            }}
          >
            <CloseIcon size={14} />
          </button>
        ) : null}
      </div>
    </div>
  );
}
